import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { WHATSAPP_MESSAGE_SOURCE, formatPhoneDisplay, skipReasonLabel } from '../../utils/whatsapp';

/** One person: name (or just the number) as plain text. */
const Person = ({ row }) => (
  <span className="min-w-0">
    <span className="block text-base text-foreground break-words">{row.name || formatPhoneDisplay(row.phone)}</span>
    {row.name && <span className="block text-sm text-muted-foreground">{formatPhoneDisplay(row.phone)}</span>}
  </span>
);

/**
 * Step 2: who will really be messaged. The counts are the server's; ticking a person off only
 * changes the exclusions the next preview request carries.
 */
const RecipientsStep = ({ wizard }) => {
  const { preview, state, dispatch } = wizard;
  const [showSkipped, setShowSkipped] = useState(false);
  const { data, roster, loading, error } = preview;
  const skipped = data?.skipped ?? [];
  const excluded = new Set(state.excluded);

  if (!data) {
    return error ? (
      <div className="p-4 space-y-2 border border-red-200 dark:border-red-900 rounded-lg bg-red-50 dark:bg-red-950/50">
        <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        <button
          type="button"
          onClick={preview.retry}
          className="px-3 py-1 text-sm bg-card border border-input rounded-lg hover:bg-muted/50"
        >
          Try again
        </button>
      </div>
    ) : (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw size={16} className="animate-spin" /> Finding recipients...
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p data-testid="recipient-summary" className="text-base font-medium text-foreground">
          {data.sendable_count} will send, {skipped.length} skipped
        </p>
        {loading && (
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <RefreshCw size={14} className="animate-spin" /> Updating...
          </span>
        )}
      </div>
      {error && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-red-800 dark:text-red-300">
          {error}
          <button
            type="button"
            onClick={preview.retry}
            className="px-3 py-1 bg-card border border-input rounded-lg hover:bg-muted/50"
          >
            Try again
          </button>
        </div>
      )}

      {skipped.length > 0 && (
        <div>
          <button
            type="button"
            aria-expanded={showSkipped}
            onClick={() => setShowSkipped((v) => !v)}
            className="text-sm underline text-foreground/80 hover:text-foreground"
          >
            {showSkipped ? 'Hide skipped' : `Show skipped (${skipped.length})`}
          </button>
          {showSkipped && (
            <ul className="mt-2 divide-y divide-border/60 border border-border rounded-lg max-h-80 overflow-y-auto">
              {skipped.map((row, i) => (
                <li key={`${row.phone}-${i}`} className="flex items-start justify-between gap-3 p-3">
                  <Person row={row} />
                  <span className="text-sm text-muted-foreground shrink-0">{skipReasonLabel(row.reason)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {roster.length > 0 && (
        <div>
          <p className="mb-2 text-sm text-muted-foreground">Untick anyone who should not get this message.</p>
          <ul
            data-testid="sendable-list"
            className="border border-border divide-y divide-border/60 rounded-lg max-h-80 overflow-y-auto"
          >
            {roster.map((row, i) => (
              <li key={row.source_id ?? `${row.phone}-${i}`}>
                {row.source_id ? (
                  <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/50">
                    <input
                      type="checkbox"
                      className="w-4 h-4 mt-1"
                      checked={!excluded.has(row.source_id)}
                      onChange={() => dispatch({ type: 'toggleExcluded', id: row.source_id })}
                    />
                    <Person row={row} />
                  </label>
                ) : (
                  <div className="p-3">
                    <Person row={row} />
                  </div>
                )}
              </li>
            ))}
          </ul>
          {state.source === WHATSAPP_MESSAGE_SOURCE.CSV && (
            <p className="mt-2 text-sm text-muted-foreground">
              To drop someone from an imported list, edit the list on the first step.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientsStep;
